"use client";

import { ArrowNorthWest, Close, Delete, FeatureSearch, Search } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import {
	type ChangeEvent,
	type FormEvent,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useRef,
} from "react";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { Link, useRouter } from "@/i18n/routing";
import { useRecentSearches } from "../hooks/useRecentSearches";
import {
	usePopularCategories,
	usePopularProducts,
	useSearchResults,
} from "../hooks/useSearchResults";
import { useSearchModalConfig, useSearchQuery } from "../SearchProvider";
import { CategoryPills } from "./CategoryPills";
import { ProductCarousel } from "./ProductCarousel";

export const SearchPanel = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
	const t = useTranslations();
	const router = useRouter();
	const inputRef = useRef<HTMLInputElement>(null);
	const { query, setQuery } = useSearchQuery();
	const { popularTopics } = useSearchModalConfig();
	const trimmed = query.trim();

	const { recent, add: addRecent, remove: removeRecent } = useRecentSearches();
	const { data: results, isFetching, isDebouncing } = useSearchResults(query);
	const { data: popular, isPending: popularPending } = usePopularProducts();
	const { data: popularCategories, isPending: popularCategoriesPending } = usePopularCategories();

	useEffect(() => {
		if (!open) {
			setQuery("");
			return;
		}
		// On desktop the input lives in the page header (HeaderSearchInput) and
		// already has focus when the modal opens. Only focus the in-modal input
		// on mobile, where the header has no input.
		const isDesktop =
			typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
		if (isDesktop) return;
		requestAnimationFrame(() => inputRef.current?.focus());
	}, [open, setQuery]);

	const submitTo = useCallback(
		(term: string) => {
			const t = term.trim();
			if (!t) return;
			addRecent(t);
			onClose();
			router.push(`/search?q=${encodeURIComponent(t)}`);
		},
		[addRecent, onClose, router],
	);

	const onSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		submitTo(query);
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Escape") {
			e.stopPropagation();
			if (query) {
				setQuery("");
			} else {
				onClose();
			}
		}
	};

	const onChange = (e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value);

	const hasResults =
		!!results &&
		(results.products.length > 0 ||
			results.relatedPages.length > 0 ||
			results.relatedCategories.length > 0 ||
			results.relatedFaqs.length > 0);
	const totalHits = (results?.nHits ?? 0) + (results?.relatedPagesCount ?? 0);
	const isResultsState = trimmed.length > 0 && hasResults;
	// While the initial search request is in flight (or in the debounce window
	// before it fires) show the *default* sections — popular topics, popular
	// categories, popular products — as a backdrop with a spinner overlay on
	// top, instead of rendering nothing.
	const isLoadingState = trimmed.length > 0 && (isFetching || isDebouncing) && !hasResults;
	// Pure no-results state: query has settled, nothing matched.
	const isNoResultsState =
		trimmed.length > 0 && !isFetching && !isDebouncing && results && !hasResults;
	// Default state renders whenever the input is empty. The popular sections
	// have their own loading-state skeletons (driven by `popularPending` /
	// `popularCategoriesPending`), so on first open — before any data has
	// arrived from Depict — the section headings render and the carousel /
	// pills show skeleton placeholders, rather than the whole panel being
	// empty. `!isFetching` keeps the default state hidden if a *previous*
	// in-flight search is still resolving (we show the loading-overlay
	// branch above for that case).
	const isDefaultState = !trimmed && !isFetching;
	const showResultsMeta = isResultsState && results;

	return (
		<>
			<header className="shrink-0">
				{/* Desktop-only top bar: result count + See all results when results exist. */}
				{showResultsMeta && (
					<Container
						as="div"
						className="hidden lg:flex items-center gap-grid-component-spacing-md h-16 border-b border-border-medium"
					>
						<Typography variant="body-xs-reg" as="span" className="text-text-dark-text-1">
							{totalHits} {t("results for")}{" "}
							<strong className="font-semibold">&quot;{trimmed}&quot;</strong>
						</Typography>
						<div className="ml-auto flex items-center gap-grid-component-spacing-md">
							<Button
								as={Link}
								href={`/search?q=${encodeURIComponent(trimmed)}`}
								onClick={onClose}
								size="medium"
							>
								{t("See all results")}
							</Button>
						</div>
					</Container>
				)}
				{/* Mobile/tablet: input form at the top of the panel (no header bar).
				    Border lives on this wrapper so it reaches edge-to-edge regardless
				    of the Container gutters. */}
				<div className="border-y border-border-medium lg:hidden">
					<Container as="div">
						<form onSubmit={onSubmit} className="flex items-center gap-grid-component-spacing-md">
							<label className="flex flex-1 items-center gap-grid-component-spacing-sm h-12">
								<Search className="size-5 text-text-dark-text-1" />
								<input
									ref={inputRef}
									type="text"
									name="q"
									value={query}
									onChange={onChange}
									onKeyDown={onKeyDown}
									placeholder={t("Search for products, pages, and stories")}
									className="flex-1 min-w-0 bg-transparent outline-none text-base text-text-dark-text-1 placeholder:text-text-sand-text-3"
									autoComplete="off"
									spellCheck={false}
								/>
								{query ? (
									<IconButton
										icon={Close}
										label={t("Clear")}
										variant="ghost-primary"
										size="md"
										className="text-text-dark-text-1"
										onClick={() => {
											setQuery("");
											inputRef.current?.focus();
										}}
									/>
								) : (
									<IconButton
										icon={Close}
										label={t("Close")}
										variant="ghost-primary"
										size="md"
										className="text-text-dark-text-1"
										onClick={onClose}
									/>
								)}
							</label>
						</form>
					</Container>
				</div>
			</header>

			<div className="flex-1 overflow-y-auto overscroll-contain relative">
				{isLoadingState && (
					<>
						<DefaultState
							recent={recent}
							onPickRecent={submitTo}
							onRemoveRecent={removeRecent}
							popularCategories={popularCategories ?? []}
							popularCategoriesLoading={popularCategoriesPending}
							popularProducts={popular?.products ?? []}
							popularLoading={popularPending}
							popularTopics={popularTopics}
							onClose={onClose}
							onPrefill={(term) => setQuery(term)}
						/>
						<div className="absolute inset-0 flex items-start justify-center pt-grid-component-spacing-2xl bg-background-bg-sand-1/70 backdrop-blur-[1px] pointer-events-none">
							<Spinner />
						</div>
					</>
				)}
				{isDefaultState && (
					<DefaultState
						recent={recent}
						onPickRecent={submitTo}
						onRemoveRecent={removeRecent}
						popularCategories={popularCategories ?? []}
						popularCategoriesLoading={popularCategoriesPending}
						popularProducts={popular?.products ?? []}
						popularLoading={popularPending}
						popularTopics={popularTopics}
						onClose={onClose}
						onPrefill={(term) => setQuery(term)}
					/>
				)}
				{isResultsState && results && (
					<ResultsState
						query={trimmed}
						didYouMean={results.didYouMean}
						relatedPages={results.relatedPages}
						relatedCategories={results.relatedCategories}
						relatedFaqs={results.relatedFaqs}
						relatedProducts={results.products}
						resultsLoading={isFetching}
						onPickDidYouMean={(term) => setQuery(term)}
						onClose={onClose}
					/>
				)}
				{isNoResultsState && (
					<NoResultsState
						onClear={() => {
							setQuery("");
							inputRef.current?.focus();
						}}
					/>
				)}
			</div>

			{showResultsMeta && (
				<footer className="shrink-0 bg-background-bg-sand-1 border-t border-border-medium lg:hidden">
					<Container as="div" className="py-grid-component-spacing-sm">
						<Button
							as={Link}
							href={`/search?q=${encodeURIComponent(trimmed)}`}
							onClick={onClose}
							number={totalHits}
							className="w-full"
						>
							{t("See all results")}
						</Button>
					</Container>
				</footer>
			)}
		</>
	);
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
	<Typography
		variant="body-xs-reg"
		as="h3"
		className="text-text-dark-text-2 uppercase tracking-wider text-xs"
	>
		{children}
	</Typography>
);

const Spinner = ({ className }: { className?: string }) => (
	<svg
		className={clsx("size-8 animate-spin text-text-dark-text-1", className)}
		xmlns="http://www.w3.org/2000/svg"
		fill="none"
		viewBox="0 0 24 24"
		aria-hidden="true"
	>
		<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
		<path
			className="opacity-75"
			fill="currentColor"
			d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
		/>
	</svg>
);

const RELATED_PAGES_SKELETON_COUNT = 4;
const RELATED_PAGES_SKELETON_WIDTHS = ["75%", "60%", "85%", "55%"];

const RelatedPagesSkeleton = () => (
	<ul className="flex flex-col">
		{Array.from({ length: RELATED_PAGES_SKELETON_COUNT }).map((_, i) => (
			<li
				// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
				key={i}
				className="border-b border-border-medium last:border-b-0 py-grid-component-spacing-sm"
			>
				<Skeleton className="h-4" width={RELATED_PAGES_SKELETON_WIDTHS[i]} />
			</li>
		))}
	</ul>
);

const DefaultState = ({
	recent,
	onPickRecent,
	onRemoveRecent,
	popularCategories,
	popularCategoriesLoading,
	popularProducts,
	popularLoading,
	popularTopics,
	onPrefill,
	onClose,
}: {
	recent: string[];
	onPickRecent: (term: string) => void;
	onRemoveRecent: (term: string) => void;
	popularCategories: {
		listingId: string;
		title: string;
		slug: string | null;
		baseCategory: string | null;
	}[];
	popularCategoriesLoading?: boolean;
	popularProducts: import("@ellaandil/formatters").ProductCard[];
	popularLoading?: boolean;
	popularTopics: string[];
	onPrefill: (term: string) => void;
	onClose: () => void;
}) => {
	const t = useTranslations();
	const hasRecent = recent.length > 0;
	return (
		<Container
			as="div"
			className="grid gap-grid-component-spacing-xl py-grid-component-spacing-lg lg:grid-cols-[1fr_1px_1fr]"
		>
			<section className="flex flex-col gap-grid-component-spacing-md min-w-0">
				{hasRecent ? (
					<>
						<SectionHeading>{t("Recent search")}</SectionHeading>
						<ul className="flex flex-col gap-grid-component-spacing-2xs">
							{recent.map((term) => (
								<li key={term} className="group flex items-center justify-between min-h-6">
									<button
										type="button"
										className="min-w-0 flex-1 text-left text-sm text-text-dark-text-1 truncate group-hover:underline group-hover:decoration-border-dark group-hover:underline-offset-[3px]"
										onClick={() => onPickRecent(term)}
									>
										{term}
									</button>
									<div className="flex shrink-0 items-center gap-0.5">
										<IconButton
											icon={ArrowNorthWest}
											label={t("Use this search")}
											variant="ghost-primary"
											size="sm"
											onClick={() => onPrefill(term)}
										/>
										<IconButton
											icon={Delete}
											label={t("Remove from recent searches")}
											variant="ghost-primary"
											size="sm"
											onClick={() => onRemoveRecent(term)}
										/>
									</div>
								</li>
							))}
						</ul>
					</>
				) : popularTopics.length > 0 ? (
					<>
						<SectionHeading>{t("Popular search topics")}</SectionHeading>
						<ul className="flex flex-col gap-grid-component-spacing-2xs">
							{popularTopics.map((topic) => (
								<li key={topic} className="group flex items-center justify-between min-h-6">
									<Link
										href={`/search?q=${encodeURIComponent(topic)}`}
										onClick={onClose}
										className="min-w-0 flex-1 text-left text-sm text-text-dark-text-1 truncate unstyled group-hover:underline group-hover:decoration-border-dark group-hover:underline-offset-[3px]"
									>
										{topic}
									</Link>
									<div className="flex shrink-0 items-center gap-0.5">
										<IconButton
											icon={ArrowNorthWest}
											label={t("Use this search")}
											variant="ghost-primary"
											size="sm"
											onClick={() => onPrefill(topic)}
										/>
									</div>
								</li>
							))}
						</ul>
					</>
				) : null}
			</section>

			<div aria-hidden="true" className="hidden lg:block bg-border-medium" />

			<section className="flex flex-col gap-grid-component-spacing-md min-w-0">
				<SectionHeading>{t("Popular categories")}</SectionHeading>
				<CategoryPills
					items={popularCategories}
					loading={popularCategoriesLoading}
					onItemClick={onClose}
				/>
			</section>

			<section className="flex flex-col gap-grid-component-spacing-md min-w-0 lg:col-span-3">
				<SectionHeading>{t("Popular products")}</SectionHeading>
				<ProductCarousel products={popularProducts} loading={popularLoading} />
			</section>
		</Container>
	);
};

const ResultsState = ({
	query,
	didYouMean,
	relatedPages,
	relatedCategories,
	relatedFaqs,
	relatedProducts,
	resultsLoading,
	onPickDidYouMean,
	onClose,
}: {
	query: string;
	didYouMean: string | null;
	relatedPages: { title: string; description: string; url: string; imageUrl?: string }[];
	relatedCategories: {
		listingId: string;
		title: string;
		slug: string | null;
		baseCategory: string | null;
	}[];
	relatedFaqs: { id: string; question: string; answerSnippet: string; url: string }[];
	relatedProducts: import("@ellaandil/formatters").ProductCard[];
	resultsLoading?: boolean;
	onPickDidYouMean: (term: string) => void;
	onClose: () => void;
}) => {
	const t = useTranslations();
	return (
		<Container
			as="div"
			className="flex flex-col gap-grid-component-spacing-xl py-grid-component-spacing-lg"
		>
			{didYouMean && (
				<div className="text-sm text-text-dark-text-2">
					{t("Did you mean")}:{" "}
					<button
						type="button"
						onClick={() => onPickDidYouMean(didYouMean)}
						className="text-text-dark-text-1 underline"
					>
						&quot;{didYouMean}&quot;
					</button>
				</div>
			)}

			<div className="grid gap-grid-component-spacing-xl lg:grid-cols-2">
				{(relatedPages.length > 0 || resultsLoading) && (
					<section className="flex flex-col gap-grid-component-spacing-md min-w-0">
						<SectionHeading>{t("Related pages")}</SectionHeading>
						{resultsLoading && !relatedPages.length ? (
							<RelatedPagesSkeleton />
						) : (
							<ul className="flex flex-col">
								{relatedPages.map((p) => (
									<li
										key={p.url}
										className="border-b border-border-medium last:border-b-0 py-grid-component-spacing-sm"
									>
										<Link href={p.url} onClick={onClose} className="unstyled block">
											<div className="text-sm text-text-dark-text-1">{p.title}</div>
											{p.description && (
												<p className="text-xs text-text-dark-text-2 mt-1">{p.description}</p>
											)}
										</Link>
									</li>
								))}
							</ul>
						)}
					</section>
				)}

				{(relatedCategories.length > 0 || resultsLoading) && (
					<section className="flex flex-col gap-grid-component-spacing-md min-w-0">
						<SectionHeading>{t("Related categories")}</SectionHeading>
						<CategoryPills
							items={relatedCategories}
							loading={resultsLoading}
							onItemClick={onClose}
						/>
					</section>
				)}

				{relatedFaqs.length > 0 && (
					<section className="flex flex-col gap-grid-component-spacing-md min-w-0 lg:col-span-2">
						<SectionHeading>{t("Helpful answers")}</SectionHeading>
						<ul className="flex flex-col">
							{relatedFaqs.map((faq) => (
								<li
									key={faq.id}
									className="border-b border-border-medium last:border-b-0 py-grid-component-spacing-sm"
								>
									<Link href={faq.url} onClick={onClose} className="unstyled block">
										<div className="text-sm text-text-dark-text-1">{faq.question}</div>
										{faq.answerSnippet && (
											<p className="text-xs text-text-dark-text-2 mt-1 line-clamp-2">
												{faq.answerSnippet}
											</p>
										)}
									</Link>
								</li>
							))}
						</ul>
					</section>
				)}
			</div>

			{(relatedProducts.length > 0 || resultsLoading) && (
				<section className="flex flex-col gap-grid-component-spacing-md min-w-0">
					<SectionHeading>{t("Related products")}</SectionHeading>
					<ProductCarousel products={relatedProducts} loading={resultsLoading} />
				</section>
			)}
		</Container>
	);
};

const NoResultsState = ({ onClear }: { onClear: () => void }) => {
	const t = useTranslations();
	return (
		<div className="px-grid-component-spacing-md pt-grid-component-spacing-2xl pb-grid-component-spacing-5xl">
			<div
				className="mx-auto flex flex-col items-center gap-grid-component-spacing-2xl text-center"
				style={{ width: "100%", maxWidth: "28rem" }}
			>
				<FeatureSearch className="size-8 text-text-dark-text-1" />
				<div className="flex w-full flex-col gap-grid-component-spacing-2xs text-text-dark-text-1">
					<Typography variant="heading-lg" as="h3">
						{t("No results")}
					</Typography>
					<Typography variant="body-md-reg" as="p">
						{t(
							"Unfortunately, we did not find any matches for your search. Try to reformulate your search and try again.",
						)}
					</Typography>
				</div>
				<Button type="button" size="large" onClick={onClear}>
					{t("Clear search")}
				</Button>
			</div>
		</div>
	);
};
